import {
  RevCard,
  RevStatusBadge,
  RevTooltip,
  RevWhiteDisclosure,
} from "@revmatics/catalyst";
import { PlatformInfoBlock } from "./PlatformInfoBlock.tsx";
import { PrimaryTitleBlock } from "./PrimaryTitleBlock.tsx";
import { ProgressLine } from "./ProgressLine.tsx";
import { ScoreCardBlock } from "./ScoreCardBlock.tsx";
import {
  faChartLineUp,
  faHeart,
  faRobot,
  faWaveSquare,
} from "@fortawesome/pro-light-svg-icons";
import { ResponsiveLine } from "@nivo/line";
import { formatDateToMonthDayYear } from "../../../../../../../helpers/functions/formatDate.tsx";
import { fullFormatNumbers } from "../../../../../../../helpers/functions/formatNumber.ts";
import { CreatorByIdPlatformData } from "../../../../../../../stores/types/creatorsTypes.tsx";

type Props = {
  data: CreatorByIdPlatformData;
};

export const CreatorByIdPlatform = ({ data }: Props) => {
  return (
    <div className="flex flex-1 flex-col gap-4 justify-between">
      <div className="flex flex-col gap-4">
        <RevCard className="flex flex-col xl:flex-row gap-y-4 gap-x-6 items-start xl:items-center">
          {data.nickname && (
            <p className="typography-body-sm text-monochrome-600">
              @{data.nickname}
            </p>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-4 lg:gap-3 xl:gap-4 w-full">
            {data.followers !== null && (
              <PlatformInfoBlock
                name="Total Followers"
                value={fullFormatNumbers({
                  value: data.followers,
                  shortenValue: 1e3,
                })}
              />
            )}
            {data.engagement !== null && (
              <PlatformInfoBlock
                name="Engagement"
                value={`${fullFormatNumbers({ value: data.engagement, decimalCount: 1 })}${data.engagement === 0 ? "" : "%"}`}
              />
            )}
            {data.estimated_reach !== null && (
              <PlatformInfoBlock
                name="Estimated reach"
                value={fullFormatNumbers({
                  value: data.estimated_reach,
                  shortenValue: 1e3,
                })}
              />
            )}
          </div>
        </RevCard>
        <RevWhiteDisclosure title="Detailed AI Score Analysis">
          <div className="px-6 pt-2 pb-6 bg-white flex flex-col gap-6">
            {data.fit !== null && (
              <PrimaryTitleBlock
                title="Fit"
                description="Brand alignment and authenticity"
                additionalData={
                  <p className="typography-body-bold-sm">
                    {fullFormatNumbers({
                      value: data.fit,
                    })}
                  </p>
                }
                classNameTitle="flex justify-between items-start gap-4"
              >
                <ProgressLine
                  value={Number(
                    fullFormatNumbers({
                      value: data.fit,
                    }),
                  )}
                />
              </PrimaryTitleBlock>
            )}
            {(data.health !== null ||
              data.creator_growth ||
              data.bot_followers ||
              data.engagement_rate ||
              data.post_frequency) && (
              <PrimaryTitleBlock
                title="Health"
                description="Performance and growth metrics"
                additionalData={
                  data.health && (
                    <p className="typography-body-bold-sm">
                      {fullFormatNumbers({
                        value: data.health,
                      })}
                    </p>
                  )
                }
                classNameTitle="flex justify-between items-start gap-4"
              >
                {data.health !== null && (
                  <ProgressLine
                    value={Number(
                      fullFormatNumbers({
                        value: data.health,
                      }),
                    )}
                  />
                )}
                <div className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-1 2xl:grid-cols-2 mt-4">
                  {data.creator_growth && (
                    <ScoreCardBlock
                      title="Creator Growth"
                      value=""
                      score={`${fullFormatNumbers({
                        value: data.creator_growth.value,
                        round: 2,
                      })}${data.creator_growth.value === 0 ? "" : "%"}`}
                      scoreComment={data.creator_growth.label.toLowerCase()}
                      icon={faChartLineUp}
                    >
                      {data.stats_history && data.stats_history.length > 0 && (
                        <div className="h-7 w-full mt-1">
                          <ResponsiveLine
                            data={[
                              {
                                id: `creator-overview-${data.platform.name}-chart`,
                                data: data.stats_history.map(
                                  ({ followers, month }) => {
                                    return { x: new Date(month), y: followers };
                                  },
                                ),
                              },
                            ]}
                            defaultHeight={27}
                            lineWidth={1}
                            yFormat={`>-0.3g`}
                            enableArea={true}
                            enablePoints={false}
                            enableGridX={false}
                            enableGridY={false}
                            isInteractive={true}
                            useMesh={true}
                            enableCrosshair={false}
                            tooltip={({ point }) => {
                              return (
                                <RevTooltip
                                  position="bottom"
                                  wrapperClassName="p-0 overflow-hidden bg-white shadow-elevation-2"
                                  className="z-[9999]"
                                >
                                  <div className="flex flex-col bg-white min-w-[140px]">
                                    <div className="mx-3 py-1.5 border-b border-monochrome-200 text-center">
                                      <span className="typography-body-bold-xs text-monochrome-700">
                                        {formatDateToMonthDayYear(
                                          point.data.xFormatted,
                                        )}
                                      </span>
                                    </div>

                                    <div className="px-3 py-2 flex flex-col items-center gap-1">
                                      <p className="typography-body-xs text-monochrome-500 uppercase tracking-wider">
                                        Followers
                                      </p>
                                      <div className="flex gap-2 items-center">
                                        <div className="w-2.5 h-2.5 rounded-full bg-primary-500 shadow-sm" />
                                        <span className="typography-body-bold-sm text-monochrome-900">
                                          {fullFormatNumbers({
                                            value: point.data.yFormatted,
                                            shortenValue: 1e3,
                                            decimalCount:
                                              Number(point.data.yFormatted) >
                                              1e3
                                                ? 2
                                                : 0,
                                          })}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </RevTooltip>
                              );
                            }}
                            colors={["#1D56F780"]}
                            defs={[
                              {
                                colors: [
                                  {
                                    color: "#1D56F780",
                                    offset: 0,
                                  },
                                  {
                                    color: "#fff",
                                    offset: 100,
                                  },
                                ],
                                id: "gradient-platform-score",
                                type: "linearGradient",
                              },
                            ]}
                            fill={[
                              {
                                id: "gradient-platform-score",
                                match: "*",
                              },
                            ]}
                          />
                        </div>
                      )}
                    </ScoreCardBlock>
                  )}
                  {data.bot_followers !== null && (
                    <ScoreCardBlock
                      title="Bot Followers"
                      value=""
                      score={`${fullFormatNumbers({
                        value: data.bot_followers.value,
                        round: 2,
                      })}${data.bot_followers.value === 0 ? "" : "%"}`}
                      scoreComment={data.bot_followers.label.toLowerCase()}
                      icon={faRobot}
                    />
                  )}
                  {data.engagement_rate !== null && (
                    <ScoreCardBlock
                      title="Engagement Rate"
                      value=""
                      score={`${fullFormatNumbers({
                        value: data.engagement_rate.value,
                        round: 2,
                      })}${data.engagement_rate.value === 0 ? "" : "%"}`}
                      scoreComment={data.engagement_rate.label.toLowerCase()}
                      icon={faHeart}
                    />
                  )}
                  {data.post_frequency !== null && (
                    <ScoreCardBlock
                      title="Post Frequency (last 30 days)"
                      value=""
                      score={`${fullFormatNumbers({
                        value: data.post_frequency.value,
                        round: 2,
                      })}${data.post_frequency.value === 0 ? "" : "x"}`}
                      scoreComment={data.post_frequency.label.toLowerCase()}
                      icon={faWaveSquare}
                    />
                  )}
                </div>
              </PrimaryTitleBlock>
            )}
            <PrimaryTitleBlock
              title="Safety"
              description="Risk assessment and compliance"
              additionalData={
                <RevStatusBadge text="Coming Soon" color="info" />
              }
              classNameTitle="flex justify-between items-center gap-4"
            />
          </div>
        </RevWhiteDisclosure>
        {(data.audience_demographics !== null ||
          data.average_views !== null ||
          data.average_likes !== null ||
          data.average_comments !== null) && (
          <RevWhiteDisclosure title="Demographics & Performance">
            <div className="px-6 pt-2 pb-6 bg-white grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 2xl:grid-cols-2 gap-6">
              {data.audience_demographics && (
                <PrimaryTitleBlock title="Audience Demographics">
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 h-[103px]">
                    <div className="rounded-lg border border-monochrome-200 p-3 bg-white flex flex-col justify-center">
                      <p className="typography-body-xs text-monochrome-600">
                        Primary Age Group
                      </p>
                      <p className="typography-body-bold-sm">
                        {data.audience_demographics.primary_age_group}
                      </p>
                    </div>
                    <div className="rounded-lg border border-monochrome-200 p-3 bg-white flex flex-col justify-center">
                      <p className="typography-body-xs text-monochrome-600">
                        Female / Male
                      </p>
                      <p className="typography-body-bold-sm">
                        {fullFormatNumbers({
                          value: data.audience_demographics.gender_split.female,
                        })}
                        % /{" "}
                        {fullFormatNumbers({
                          value:
                            100 -
                            data.audience_demographics.gender_split.female,
                        })}
                        %
                      </p>
                    </div>
                  </div>
                </PrimaryTitleBlock>
              )}
              {(data.average_views !== null ||
                data.average_likes !== null ||
                data.average_comments !== null) && (
                <PrimaryTitleBlock title="Recent Performance">
                  <div className="mt-4 rounded-lg border border-monochrome-200 p-3 bg-white flex flex-col gap-2 h-[103px]">
                    {data.average_views !== null && (
                      <div className="flex justify-between items-center gap-4">
                        <p className="typography-body-xs text-monochrome-600">
                          Average Views
                        </p>
                        <p className="typography-body-bold-sm">
                          {fullFormatNumbers({
                            value: data.average_views,
                            shortenValue: 1e3,
                            decimalCount: data.average_views < 1e3 ? 0 : 1,
                          })}
                        </p>
                      </div>
                    )}
                    {data.average_likes !== null && (
                      <div className="flex justify-between items-center gap-4">
                        <p className="typography-body-xs text-monochrome-600">
                          Average Likes
                        </p>
                        <p className="typography-body-bold-sm">
                          {fullFormatNumbers({
                            value: data.average_likes,
                            shortenValue: 1e3,
                            decimalCount: data.average_likes < 1e3 ? 0 : 1,
                          })}
                        </p>
                      </div>
                    )}
                    {data.average_comments !== null && (
                      <div className="flex justify-between items-center gap-4">
                        <p className="typography-body-xs text-monochrome-600">
                          Average Comments
                        </p>
                        <p className="typography-body-bold-sm">
                          {fullFormatNumbers({
                            value: data.average_comments,
                            shortenValue: data.average_comments < 1e3 ? 0 : 1,
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                </PrimaryTitleBlock>
              )}
            </div>
          </RevWhiteDisclosure>
        )}
      </div>
    </div>
  );
};
